/**
 * postRapporter — pobranie danych z RAPPORTERA przez proxy AUDYTORA.
 *
 * RAPPORTER od 09.2026 odpowiada kodami, które dla audytu znaczą coś innego niż awaria:
 *   422 EMPTY_PERIOD / NO_FISCAL_YEAR — w okresie nie ma czego audytować (to znalezisko),
 *   503 MUTEX_BUSY / MUTEX_TIMEOUT    — wspólna sesja 24SO jest zajęta, wolno ponowić.
 * Wcześniej oba kończyły jako „HTTP 422" / „intern feil" bez słowa wyjaśnienia.
 */
import { authFetch } from './authFetch.js';
import { t } from './i18n';

// Proxy AUDYTORA zrywa po 120 s (server.js, proxyToRapporter), więc przeglądarka czeka chwilę dłużej.
export const RAPPORTER_TIMEOUT_MS = 125000;
// RAPPORTER podaje Retry-After: 30, ale proxy nie przenosi nagłówków.
const BUSY_RETRY_WAIT_MS = 30000;

const MESSAGE_KEYS = {
    EMPTY_PERIOD: 'rapporter_err_empty_period',
    NO_FISCAL_YEAR: 'rapporter_err_no_fiscal_year',
    MUTEX_BUSY: 'rapporter_err_busy',
    MUTEX_TIMEOUT: 'rapporter_err_busy',
    CLIENT_TIMEOUT: 'rapporter_err_timeout',
};
const RETRYABLE = new Set(['MUTEX_BUSY', 'MUTEX_TIMEOUT']);

const rapporterError = (code, status, fallback) => {
    const key = MESSAGE_KEYS[code];
    return Object.assign(new Error(key ? t(key) : fallback), { code: code || null, status });
};

const defaultSleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * @param {Function} fetchFn — authFetch albo jego zamiennik w teście
 * @param {string} path — np. '/generate-data'
 * @param {string} body — JSON
 * @returns {Promise<object>} dane z RAPPORTERA; błąd niesie `code` i `status`
 */
export async function postRapporter(fetchFn, path, body, { sleep = defaultSleep, isAborted } = {}) {
    for (let attempt = 0; ; attempt++) {
        let res;
        try {
            res = await fetchFn(`/api/proxy${path}`, { method: 'POST', body, timeoutMs: RAPPORTER_TIMEOUT_MS });
        } catch (err) {
            // Bez ponowienia: RAPPORTER dalej pracuje nad pierwszym żądaniem i trzyma sesję 24SO.
            if (err?.name === 'AbortError') throw rapporterError('CLIENT_TIMEOUT', 0);
            throw err;
        }
        if (res.ok) return res.json();
        const payload = await res.json().catch(() => ({}));
        if (attempt === 0 && RETRYABLE.has(payload.code)) {
            await sleep(BUSY_RETRY_WAIT_MS);
            // Batch przerwany w trakcie czekania: nie wysyłamy kolejnego żądania do 24SO.
            if (!isAborted?.()) continue;
        }
        throw rapporterError(payload.code, res.status, res._hint || payload.error || `HTTP ${res.status}`);
    }
}

export const fetchRapporter = (path, body, options) => postRapporter(authFetch, path, body, options);
